import { Role } from '../enums/role';
import { Right } from '../enums/right';

/**
 * Matrice rôle → droits.
 *
 * Source de vérité unique, partagée par l'API (calcul des droits injectés dans le
 * JWT et vérifiés par le `RightsGuard`) et par les fronts (affichage conditionnel).
 * Le front ne se fie jamais à cette matrice pour la sécurité : il l'utilise pour
 * l'ergonomie, l'API re-vérifie systématiquement.
 */
export const RIGHTS_MATRIX: Record<Role, Right[]> = {
	[Role.GUEST]: [
		Right.RIDE_CREATE,
		Right.RIDE_ESTIMATE,
		Right.RIDE_READ_OWN,
		Right.RIDE_CANCEL_OWN,
		Right.PLACE_READ,
	],

	[Role.CLIENT]: [
		Right.ACCOUNT_READ,
		Right.ACCOUNT_EDIT,
		Right.ACCOUNT_DELETE,
		Right.RIDE_CREATE,
		Right.RIDE_ESTIMATE,
		Right.RIDE_READ_OWN,
		Right.RIDE_CANCEL_OWN,
		Right.RIDE_CLAIM,
		Right.PLACE_READ,
	],

	[Role.DRIVER]: [
		Right.ACCOUNT_READ,
		Right.ACCOUNT_EDIT,
		// Un chauffeur reste un client : il peut basculer en mode rider.
		Right.RIDE_CREATE,
		Right.RIDE_ESTIMATE,
		Right.RIDE_READ_OWN,
		Right.RIDE_CANCEL_OWN,
		Right.RIDE_CLAIM,
		Right.PLACE_READ,
		// Spécifique conduite
		Right.DRIVER_MODE,
		Right.DRIVER_SELF_READ,
		Right.DRIVER_SELF_EDIT,
		Right.DRIVER_STATUS_SET,
		Right.DRIVER_POSITION_SET,
		Right.DRIVER_OFFER_READ,
		Right.DRIVER_OFFER_ANSWER,
		Right.DRIVER_RIDE_PROGRESS,
	],

	[Role.KIOSK]: [
		Right.KIOSK_SESSION,
		Right.RIDE_CREATE,
		Right.RIDE_ESTIMATE,
		Right.RIDE_READ_OWN,
		Right.RIDE_CANCEL_OWN,
		Right.PLACE_READ,
	],

	[Role.OPERATOR]: [
		Right.ACCOUNT_READ,
		Right.ACCOUNT_EDIT,
		Right.BO_ACCESS,
		Right.BO_LIVE_MAP,
		Right.STATS_READ,
		Right.PLACE_READ,
		Right.RIDE_READ_ALL,
		Right.RIDE_CANCEL_ALL,
		Right.RIDE_ASSIGN,
		Right.RIDE_EXPORT,
		Right.DRIVER_READ_ALL,
		Right.VEHICLE_READ_ALL,
		Right.KIOSK_READ_ALL,
		Right.USER_READ_ALL,
		// L'exploitation consulte les tarifs et les réglages de diffusion — elle
		// travaille avec, mais ne les décide pas.
		Right.SETTINGS_READ,
	],

	[Role.ADMIN]: [
		Right.ACCOUNT_READ,
		Right.ACCOUNT_EDIT,
		Right.BO_ACCESS,
		Right.BO_LIVE_MAP,
		Right.STATS_READ,
		Right.PLACE_READ,
		Right.PLACE_EDIT,
		Right.RIDE_READ_ALL,
		Right.RIDE_CANCEL_ALL,
		Right.RIDE_ASSIGN,
		Right.RIDE_EXPORT,
		Right.DRIVER_READ_ALL,
		Right.DRIVER_EDIT_ALL,
		Right.DRIVER_CREATE,
		Right.VEHICLE_READ_ALL,
		Right.VEHICLE_EDIT_ALL,
		Right.KIOSK_READ_ALL,
		Right.KIOSK_EDIT_ALL,
		Right.USER_READ_ALL,
		Right.USER_EDIT_ALL,
		Right.USER_DELETE_ALL,
		Right.USER_LOGIN_AS,
		Right.RIGHTS_READ,
		Right.SETTINGS_READ,
		Right.SETTINGS_EDIT,
	],
};

/**
 * Calcule les droits effectifs d'un utilisateur : réunion des droits de ses rôles.
 *
 * Il n'existe volontairement aucune dérogation par utilisateur, ni aucune table
 * d'association en base. La matrice ci-dessus est l'unique source de vérité :
 * pour changer ce qu'un rôle peut faire, on modifie ce fichier — le changement
 * est relu, versionné et testé, jamais appliqué à chaud sur un compte isolé.
 */
export function computeRights(roles: (Role | string)[]): Right[] {
	const granted = new Set<string>();

	for (const role of roles ?? []) {
		for (const right of RIGHTS_MATRIX[role as Role] ?? []) {
			granted.add(right);
		}
	}

	return [...granted] as Right[];
}

/** Vrai si `rights` couvre **tous** les droits demandés (ET logique). */
export function hasAllRights(rights: (Right | string)[], required: (Right | string)[]): boolean {
	const owned = new Set(rights ?? []);
	return (required ?? []).every((right) => owned.has(right));
}

/** Vrai si `rights` couvre **au moins un** des droits demandés (OU logique). */
export function hasAnyRight(rights: (Right | string)[], required: (Right | string)[]): boolean {
	const owned = new Set(rights ?? []);
	return (required ?? []).some((right) => owned.has(right));
}
